import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization/userOrganizationContext'

import { DashboardAccountingManagementStep } from '../../../../../../assets/images'
import { useOnboardingFormContext } from '../../../../contexts/OnboardingFormContext'
import { OnboardingAccountingManagement } from '../../../../enums/onboardingAccountingManagement'
import { usePredefinedOnboardingStepValue } from '../../../../hooks/usePredefinedOnboardingStepValue'
import { useRegisterOnboardingStep } from '../../../../hooks/useRegisterOnboardingStep'
import { useSubmitOnboardingStepForm } from '../../../../hooks/useSubmitOnboardingStepForm'
import { useTrackOnboarding } from '../../../../hooks/useTrackOnboarding'
import { CompanyOnboarding } from '../../../../types/companyOnboarding'
import { OnboardingForm } from '../../../../types/onboardingForm'
import { SingleCardSelect } from '../SingleCardSelect'
import { StepContainer } from '../StepContainer'
import { getAccountingManagementOptions } from './utils/getAccountingManagementOptions'

const accountingManagementAnalyticsNames: Record<OnboardingAccountingManagement, string> = {
  [OnboardingAccountingManagement.Myself]: 'myself',
  [OnboardingAccountingManagement.SplitWorkWithAdvisor]: 'mixed',
  [OnboardingAccountingManagement.Advisor]: 'accountant',
  [OnboardingAccountingManagement.AccountingFirm]: 'outsourced',
}

const selectAccountingManagementValue = (onboarding?: CompanyOnboarding): Partial<OnboardingForm> => ({
  accountingManagement: onboarding?.accountingManagement ?? undefined,
})

const buildAccountingManagementPayload = (values: OnboardingForm) =>
  values.accountingManagement ? { accountingManagement: values.accountingManagement } : undefined

export const AccountingManagementStep = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { onboardingForm } = useOnboardingFormContext()
  const { clearErrors, FormItem } = onboardingForm
  const { trackOnboarding } = useTrackOnboarding()

  const { isLoading: isLoadingPredefinedValues, predefinedValues } = usePredefinedOnboardingStepValue(
    selectAccountingManagementValue,
  )
  const { isSubmitting, submitForm } = useSubmitOnboardingStepForm(buildAccountingManagementPayload)

  const submitHandler = useCallback(
    async (values: OnboardingForm) => {
      await submitForm(values)
    },
    [submitForm],
  )

  useRegisterOnboardingStep({
    isLoading: isLoadingPredefinedValues,
    isSubmitting,
    predefinedValues,
    submitHandler,
  })

  const options = useMemo(() => getAccountingManagementOptions(t, organization), [organization, t])

  return (
    <StepContainer
      image={<DashboardAccountingManagementStep />}
      title={t('onboarding.steps.accounting_management.title')}
    >
      <FormItem
        name="accountingManagement"
        render={({ field: { error, onChange, value } }) => (
          <SingleCardSelect
            disabled={isLoadingPredefinedValues}
            hasError={!!error}
            onChange={(id) => {
              onChange(id)
              clearErrors('accountingManagement')
              trackOnboarding('Accounting management selected', {
                selectedAccountingManagement: accountingManagementAnalyticsNames[id],
              })
            }}
            options={options}
            value={value}
          />
        )}
      />
    </StepContainer>
  )
}
